import { useEffect } from 'react';

import { useAuth } from '@/Bridges/GLUtils';
import { useModalLogout } from '@/Contexts/GLModalLogout';
import {
  LOCAL_STORAGE_KEY_EXTERNAL_TIME,
  useCheckTimeManipulation,
} from '@group-link-one/gl-components';
import { syncClockOffset } from '@/Services/useAxiosHTTPRequest';

/**
 * Side effects for authenticated routes
 * - Idle logout setup
 * - Token validation
 * - Clock offset sync (X-Timestamp)
 * - User groups sync
 */
export const useRouteEffects = () => {
  const { accessToken, accessTokenIsValid } = useAuth();

  const { handleOpenModalLogout, modalLogoutIsOpenOpen } = useModalLogout();
  const { syncOffsetFromTrace } = useCheckTimeManipulation();

  // Handle invalid token
  useEffect(() => {
    if (accessToken && !accessTokenIsValid && !modalLogoutIsOpenOpen) {
      handleOpenModalLogout();
    }
  }, [accessToken, accessTokenIsValid, modalLogoutIsOpenOpen, handleOpenModalLogout]);

  // Measure the clock offset on boot, before login: every request stamps
  // `X-Timestamp` with `Date.now() + offset` (see useAxiosHTTPRequest). Shared with
  // the `INVALID_TIMESTAMP` re-sync, so early failures wait for this same fetch.
  // Runs once — the hook returns new function identities on every render.
  useEffect(() => {
    void syncClockOffset(syncOffsetFromTrace);

    // Drop the frozen instant stored by the legacy clock probe.
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY_EXTERNAL_TIME);
    } catch {
      // Storage unavailable — nothing to clean up.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
